import { Planable } from "./planableModel";
import { DEFAULT_CLUSTER_OBJECT } from "./learningObjectivesConstants";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { clusterManager } from "./clusterManager";
import { cloneDeep, isEqual, remove } from "lodash";
import { createClusterLearningObjective } from "./clusterLearningObjectiveModel";

// When we have the time to refactor this, we should consider using the same approach as in the clusterManager use es6 modules instead of Classes
export class Cluster extends Planable {
  id;
  school_id;
  name;
  description;
  type;
  user;
  shortage;
  subjects;
  education_year_levels;
  editable;
  lesson_class;
  library_learning_objectives;
  temporary;
  library_cluster_id;
  videos;
  links;
  attachments;
  initialValue = {};
  library_learning_objective_ids;
  meta = {};

  constructor(data = {}) {
    super(data);

    this.id = data.id || this.generateTempId();
    this.name = data.name || DEFAULT_CLUSTER_OBJECT.name;
    this.type = data.type || DEFAULT_CLUSTER_OBJECT.type;
    this.school_id = data.school_id || DEFAULT_CLUSTER_OBJECT.school_id;
    this.description = data.description || DEFAULT_CLUSTER_OBJECT.description;
    this.user = data.user || DEFAULT_CLUSTER_OBJECT.user;
    this.shortage = data.shortage || DEFAULT_CLUSTER_OBJECT.shortage;
    this.subjects = data.subjects || DEFAULT_CLUSTER_OBJECT.subjects;
    this.education_year_levels =
      data.education_year_levels ||
      DEFAULT_CLUSTER_OBJECT.education_year_levels;
    this.editable = data.editable ?? DEFAULT_CLUSTER_OBJECT.editable;
    this.library_learning_objectives =
      data.library_learning_objectives ||
      DEFAULT_CLUSTER_OBJECT.library_learning_objectives;
    this.lesson_class =
      data.lesson_class || DEFAULT_CLUSTER_OBJECT.lesson_class;
    this.temporary = data.temporary ?? DEFAULT_CLUSTER_OBJECT.temporary;
    this.library_cluster_id =
      data.library_cluster_id || DEFAULT_CLUSTER_OBJECT.library_cluster_id;
    this.subject_name =
      data.subject_name ||
      data.subject_shortage ||
      DEFAULT_CLUSTER_OBJECT.subject_name;
    this.subject_shortage =
      data.subject_shortage || DEFAULT_CLUSTER_OBJECT.subject_shortage;
    this.meta.can_update =
      data.meta?.can_update !== undefined
        ? data.meta.can_update
        : DEFAULT_CLUSTER_OBJECT.meta.can_update;
    this.meta.can_delete =
      data.meta?.can_delete !== undefined
        ? data.meta.can_delete
        : DEFAULT_CLUSTER_OBJECT.meta.can_delete;
    this.videos = data.videos || DEFAULT_CLUSTER_OBJECT.videos;
    this.attachments = data.attachments || DEFAULT_CLUSTER_OBJECT.attachments;
    this.library_learning_objective_ids =
      data.library_learning_objective_ids ||
      DEFAULT_CLUSTER_OBJECT.library_learning_objective_ids;
    this.links = data.links || DEFAULT_CLUSTER_OBJECT.links;

    this.setClusterLearningObjectives(data.learning_objectives);

    // planning meta data
    this.planningMeta.isDraggable = true;
    this.planningMeta.canDrag = true;
    this.planningMeta.manager = clusterManager.value;
    this.planningMeta.groupId = this.lesson_class;

    this.updateInitialValue();
  }

  setClusterLearningObjectives(learningObjectives) {
    this.learning_objectives = cloneDeep(
      DEFAULT_CLUSTER_OBJECT.learning_objectives,
    );

    if (
      !learningObjectives ||
      !Array.isArray(learningObjectives) ||
      !learningObjectives.length
    ) {
      return;
    }

    // This will create and add all the learning objectives to the cluster
    learningObjectives.forEach((learningObjective) => {
      this.learning_objectives.push(
        createClusterLearningObjective({
          ...learningObjective,
          cluster_id: this.id,
        }),
      );
    });
  }

  async addLearningObjectivesToModel(addedLearningObjectives) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { postLearningObjectivesToModel } = learningObjectivesStore;

    if (this.id && !this.temporary) {
      // Existing cluster, so post the learning objectives to the model
      await postLearningObjectivesToModel(addedLearningObjectives, this);
    } else {
      // New cluster, so we need to add the learning objectives to the cluster
      this.learning_objectives.push(
        ...addedLearningObjectives.filter(
          (learningObjective) =>
            !this.learning_objectives.some(
              (existingLearningObjective) =>
                existingLearningObjective.id === learningObjective.id,
            ),
        ),
      );
    }
  }

  async addClusterLearningObjective(learningObjective) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { addLearningObjective } = learningObjectivesStore;

    try {
      const { data } = await addLearningObjective({
        learningObjective,
        clusterId: this.id,
      });

      this.learning_objectives.push(createClusterLearningObjective(data));
    } catch (error) {
      console.log("🚀 ~ Cluster ~ addClusterLearningObjective ~ error:", error);
    }
  }

  async removeLearningObjectiveFromCluster(learningObjective) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { deleteLearningObjective } = learningObjectivesStore;

    try {
      // Remove the learning objective from the cluster
      await deleteLearningObjective(learningObjective);
      remove(this.learning_objectives, ({ id }) => id === learningObjective.id);
    } catch (e) {
      throw new Error(e);
    }
  }

  getAdditionalData() {
    const { lesson_class, subject_shortage } = this;

    return {
      lesson_class,
      subject_shortage,
    };
  }

  /**
   * This function returns the values that we want to check for changes
   * @returns {any}
   */
  valuesToCheckForChanges() {
    // We remove some values that we don't want to check for changes
    const {
      planningMeta: {
        manager,
        initialDragState,
        groupIndex,
        ...planningMetaValuesToCheck
      },
      initialValue,
      user,
      subjects,
      learning_objectives,
      library_learning_objectives,
      library_cluster_id,
      education_year_levels,
      ...clusterValuesToCheck
    } = this;

    return Object.assign({}, clusterValuesToCheck, {
      planningMeta: planningMetaValuesToCheck,
    });
  }

  /**
   * This function updates the initial value without the reference
   * @returns {any}
   */
  updateInitialValue() {
    this.initialValue = cloneDeep(this.valuesToCheckForChanges());
  }

  updateStartWeekByIndex(
    newStartIndex,
    currentStartWeekIndex,
    removeWeekFromRow,
    addWeekToRow,
  ) {
    const diff = Math.abs(newStartIndex - currentStartWeekIndex);
    const isExpanding = newStartIndex > currentStartWeekIndex;

    for (let i = 1; i <= diff; i++) {
      if (isExpanding) {
        // First set the new start week and year
        this.setStartWeekAndYear(currentStartWeekIndex + i);

        // Add a new column to the row before this cluster to fill the gap
        addWeekToRow(
          this,
          this.planningMeta.rowIndex,
          this.planningMeta.weekIndexes[0],
        );

        continue;
      }

      // First remove the week from the curent row and after we update the end week and year
      removeWeekFromRow(
        this.planningMeta.rowIndex,
        currentStartWeekIndex + -i,
        this.planningMeta.groupIndex,
      );

      // Update the start week and year
      this.setStartWeekAndYear(currentStartWeekIndex + -i);
    }
  }

  updateEndWeekByIndex(
    newEndIndex,
    currentEndWeekIndex,
    removeWeekFromRow,
    addWeekToRow,
  ) {
    const diff = Math.abs(newEndIndex - currentEndWeekIndex);
    const isExpanding = newEndIndex > currentEndWeekIndex;

    // First remove the week from the curent row and after we update the end week and year
    for (let i = 1; i <= diff; i++) {
      if (isExpanding) {
        // Remove the first row after this cluster
        removeWeekFromRow(
          this.planningMeta.rowIndex,
          currentEndWeekIndex + i,
          this.planningMeta.groupIndex,
        );

        // Update the end week and year
        this.setEndWeekAndYear(currentEndWeekIndex + i);

        continue;
      }

      // Add a new column to the row after this cluster
      this.setEndWeekAndYear(currentEndWeekIndex + -i);

      addWeekToRow(
        this,
        this.planningMeta.rowIndex,
        this.planningMeta.weekIndexes[this.planningMeta.weekIndexes.length - 1],
        true,
      );
    }
  }

  setEndWeekAndYear(index) {
    const { week, year } = this.getWeekByIndex(index);

    this.end_week = week;
    this.end_year = year;

    this.generateWeekIndexes();
  }

  setStartWeekAndYear(index) {
    const { week, year } = this.getWeekByIndex(index);

    this.start_week = week;
    this.start_year = year;

    this.generateWeekIndexes();
  }

  getStartWeekIndex() {
    return this.getWeekIndex(this.start_week, this.start_year);
  }

  getEndWeekIndex() {
    return this.getWeekIndex(this.end_week, this.end_year);
  }

  async delete() {
    if (this.temporary) {
      return Promise.resolve();
    }

    const learningObjectivesStore = useLearningObjectivesStore();
    const { deleteCluster } = learningObjectivesStore;

    await deleteCluster(this.id);
  }

  async update() {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { updateCluster } = learningObjectivesStore;

    // Hide meta en weekIndexes from the update request
    const { planningMeta, initialValue, ...rest } = this;

    try {
      const { data } = await updateCluster({
        ...rest,
        row_index: this.planningMeta.rowIndex,
      });

      const { learning_objectives, ...clusterData } = data;

      Object.assign(this, { ...clusterData, temporary: false });

      this.updateInitialValue();
    } catch (error) {
      console.log("🚀 ~ Cluster ~ update ~ error:", error);
      return Promise.reject(error);
    }
  }

  async store() {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { storeClusterFromLibrary, storeCluster } = learningObjectivesStore;

    // Hide meta en weekIndexes from the update request
    const { planningMeta, initialValue, ...rest } = this;

    try {
      const { data } =
        this.library_cluster_id == null
          ? await storeCluster({
              ...rest,
              row_index: this.planningMeta.rowIndex,
            })
          : await storeClusterFromLibrary({
              ...rest,
              row_index: this.planningMeta.rowIndex,
            });

      const { learning_objectives, ...clusterData } = data;

      Object.assign(this, { ...clusterData, temporary: false });

      this.setClusterLearningObjectives(learning_objectives);

      this.updateInitialValue();
    } catch (error) {
      console.log("🚀 ~ Cluster ~ upsert ~ error:", error, this);
      return Promise.reject(error);
    }
  }

  hasChanges() {
    return !isEqual(this.initialValue, this.valuesToCheckForChanges());
  }

  /**
   * This function updates the cluster that have been changed
   * @returns {any}
   */
  async upsert() {
    // Early return if there are no changes
    if (!this.hasChanges()) {
      return;
    }

    return this.temporary ? await this.store() : await this.update();
  }
}
